import React, { useEffect } from "react";
import mermaid from "mermaid";

export default function StateMachineDiagram({ project }) {
  const mermaidStr = constructDiagramStr(project);

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: true,
      htmlLabels: true,
      state: {
        useMaxWidth: false,
      },
    });

    mermaid.contentLoaded();
  }, [mermaidStr]);

  return (
    <div className="mermaid-content">
      <pre className="mermaid mermaid-content">{mermaidStr}</pre>
    </div>
  );
}

function defineDiagramClasses() {
  // For styling Mermaid diagram nodes
  const classDef = `
        classDef headNode font-weight:bold,stroke-width:0.5px,stroke:black,fill:green,color:white
        classDef node font-weight:regular,stroke-width:0.5px,fill:#f3f4f6
        classDef projCompleted font-weight:bold,stroke-width:0.5px,stroke:lightgreen,fill:lightgreen
        classDef projOnHold font-weight:bold,stroke-width:0.5px,stroke:orange,fill:orange
        classDef projInProgress font-weight:bold,stroke-width:0.5px,stroke:lightblue,fill:lightblue,color:black
        classDef projNew font-weight:bold,stroke-width:0.5px,stroke:yellow,fill:yellow,color:black
        classDef projDuplicate font-weight:bold,stroke-width:0.5px,stroke:maroon,fill:maroon,color:white
        classDef projWaitingForFeedback font-weight:bold,stroke-width:0.5px,stroke:purple,fill:purple,color:white
        classDef approvedNode font-weight:regular,stroke-width:0.5px,fill:green,color:white
        classDef rejectedNode font-weight:regular,stroke-width:0.5px,fill:red,color:white
        classDef waitingForApprovalNode font-weight:regular,stroke-width:0.5px,fill:purple,color:white
    `;

  return classDef;
}

// This is where the conversion of the PipelineData to string formatting readable by the diagram takes place
function constructDiagramStr(data) {
  // For applying the defined styles to nodes
  let classApplication = `
        class title headNode
    `;

  // Mermaid syntax requires class definitions to be at the bottom
  // Better to apply styles at the end before class definitions to ensure application
  let completeDiagramStr = `
        stateDiagram-v2
            direction LR

            state "${data.title}" as title              
            
            ${constructProgressDiagramStr(data, "title")}
            ${constructStageDiagramStr(data.stage, "Progress")}
            
            ${classApplication}
            ${defineDiagramClasses()}
    `;

  return completeDiagramStr;
}

function constructProgressDiagramStr(data, parentNode) {
  let classApplication = `
        class assignees node
        class initiatedOn node
        class dueOn node
    `;

  const renderStatus = (status) => {
    const dueDateState = `state "${formatDiagramStr(data.dueOn)}" as dueOn`;
    const completionDateState = `state "${formatDiagramStr(data.completedOn)}" as completedOn`;

    switch (status) {
      case "COMPLETED":
        classApplication += "\nclass status projCompleted";
        return `
                    ${completionDateState}
                    status --> completedOn: Completed On   
                `;
      case "IN PROGRESS":
        classApplication += "\nclass status projInProgress";
        return `
                    ${dueDateState}
                    status --> dueOn: Due On
                `;
      case "NEW":
        classApplication += "\nclass status projNew";
        return `
                    ${dueDateState}
                    status --> dueOn: Due On
                `;
      case "ON HOLD":
        classApplication += "\nclass status projOnHold";
        return ``;
      case "DUPLICATE":
        classApplication += "\nclass status projDuplicate";
        return `
                    ${dueDateState}
                    status --> dueOn: Due On
                `;
      case "WAITING FOR FEEDBACK":
        classApplication += "\nclass status projWaitingForFeedback";
        return `
                    ${dueDateState}
                    status --> dueOn: Due On
                `;
    }
  };

  const finalDiagramStr = `

        state "${formatDiagramStr(data.status)}" as status
        state "${formatDiagramStr(data.initiatedOn)}" as initiatedOn
        state "${formatDiagramStr(data.assignee.join(", "))}" as assignees
        ${parentNode} --> Progress

        state Progress {
            direction LR

            status --> assignees: Assignees
            status --> initiatedOn: Initiated On
             
            ${renderStatus(data.status)}
        }

        ${classApplication}
    `;

  return finalDiagramStr;
}

function constructStageDiagramStr(stateData, parentNode) {
  let appliedClassDefs = ``;

  let currentNode = "";
  let previousNode = "";
  let parsedDiagramStr = ``;

  const states = Object.keys(stateData);

  if (states.length > 0) {
    currentNode = stateData[states[0]];

    parseLoop: for (const state of states) {
      currentNode = state;

      let strToAppend = "";

      const initializedNode = `state "${formatDiagramStr(state)}" as ${state}`;
      const nodeProperties = stateData[state];
      const nodeStatus = nodeProperties["status"];

      parsedDiagramStr += `
                \n${initializedNode}

                ${
                  stateData[currentNode]["comment"].toString().valueOf() != ""
                    ? `note right of ${currentNode}\n ${stateData[currentNode]["comment"]}\n end note`
                    : ""
                } 
            `;

      if (previousNode.valueOf() != "") {
        switch (nodeStatus.valueOf()) {
          case "Approved":
            appliedClassDefs += `\nclass ${previousNode} approvedNode`;
            break;

          case "Rejected":
            appliedClassDefs += `\nclass ${currentNode} rejectedNode`;

            strToAppend = `${previousNode} --> ${currentNode}`;

            parsedDiagramStr += `\n${strToAppend}
                            ${currentNode} --> ${previousNode}: ${nodeStatus}                                                                     
                        `;
            break parseLoop;

          case "Waiting For Approval":
            appliedClassDefs += `\nclass ${previousNode} waitingForApprovalNode`;

            parsedDiagramStr += `\n${previousNode} --> ${currentNode}: ${nodeStatus}`;
            break parseLoop;

          case "In Progress":
            appliedClassDefs += `\nclass ${previousNode} waitingForApprovalNode`;

            parsedDiagramStr += `\n${previousNode} --> ${currentNode}: ${stateData[previousNode]["status"]}`;

            break parseLoop;

          case "Completed":
            appliedClassDefs += `\nclass ${previousNode},${currentNode} approvedNode`;
            parsedDiagramStr += `\n${currentNode} --> [*]: ${nodeStatus}`;
            break;
        }

        parsedDiagramStr += `
                    ${previousNode} --> ${currentNode}: ${stateData[previousNode]["status"]}                    
                `;
      } else {
        parsedDiagramStr += `
                    direction LR                    
                    ${currentNode}                                
                `;
      }

      previousNode = state;
    }

    const finalDiagramStr = `
            ${parentNode} --> Stage

            state Stage {

                ${parsedDiagramStr}                
            }

            ${appliedClassDefs}
        `;

    return finalDiagramStr;
  }
}

function formatDiagramStr(stringToModify) {
  // Chars to replace so we can avoid Mermaid syntax errors and remove special chars for diagram output
  const regExpToNone = /[[\]."]/g;
  const regExpToComma = /,/g;
  const regExpToNewLine = /;/g;

  let modifiedString = "";

  if (!stringToModify) {
    modifiedString = "Unknown"; // For undefined properties
  } else {
    modifiedString = stringToModify
      .replace(regExpToNone, "")
      .replace(regExpToComma, ", ")
      .replace(regExpToNewLine, "\n");

    // For handling dates
    if (modifiedString.match(/\d{4}-\d{2}-\d{2}/g) != null) {
      const dateParts = modifiedString.replace(/\s\d{2}:\d{2}/g, "").split("-");
      const dateProp = new Date(
        parseInt(dateParts[0]),
        parseInt(dateParts[1]) - 1,
        parseInt(dateParts[2]),
      );

      modifiedString = dateProp.toLocaleDateString("default", {
        month: "long",
        day: "2-digit",
        year: "numeric",
      });
    }
  }

  return modifiedString;
}
